import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { PageHeader, FilterCard, Field, SimpleSelect, EmptyState } from "@/components/common";
import { fmtPct, statusBg, MONTHS } from "@/lib/helpers";
import { Card, CardContent } from "@/components/ui/card";
import { CalendarRange } from "lucide-react";

export default function RekapBulan() {
  const { indikators, tahuns } = useMeta();
  const [indikator, setIndikator] = useState("");
  const [tahun, setTahun] = useState("");
  const [departemen, setDepartemen] = useState("__all__");
  const [deps, setDeps] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => { if (!indikator && indikators.length) setIndikator(indikators[0].kode); }, [indikators]);
  useEffect(() => { if (!tahun && tahuns.length) setTahun(String(tahuns[0])); }, [tahuns]);
  useEffect(() => { if (indikator) api.get(`/departemen?indikator=${encodeURIComponent(indikator)}`).then((r) => setDeps(r.data)); }, [indikator]);

  useEffect(() => {
    if (!indikator || !tahun) return;
    let url = `/rekap/bulan?indikator=${encodeURIComponent(indikator)}&tahun=${tahun}`;
    if (departemen !== "__all__") url += `&departemen=${encodeURIComponent(departemen)}`;
    api.get(url).then((r) => setData(r.data));
  }, [indikator, tahun, departemen]);

  const cell = (key, v, target) => (
    <td key={key} className={`px-2 py-1.5 text-center text-xs tabular-nums border-r border-slate-100 ${v != null ? statusBg(v, target) : "text-slate-300"}`}>
      {fmtPct(v)}
    </td>
  );

  return (
    <div data-testid="rekap-bulan-page">
      <PageHeader icon={CalendarRange} title="Rekap per Bulan" subtitle="Matriks Poliklinik × Bulan dengan kolom Rata-rata otomatis." />

      <FilterCard>
        <Field label="Indikator">
          <SimpleSelect testId="rb-indikator-select" value={indikator} onChange={setIndikator}
            options={indikators.map((i) => ({ label: i.kode, value: i.kode }))} className="w-[220px]" />
        </Field>
        <Field label="Tahun">
          <SimpleSelect testId="rb-tahun-select" value={tahun} onChange={setTahun}
            options={tahuns.map((t) => ({ label: String(t), value: t }))} className="w-[120px]" />
        </Field>
        <Field label="Departemen">
          <SimpleSelect testId="rb-departemen-select" value={departemen} onChange={setDepartemen}
            options={[{ label: "Semua Departemen", value: "__all__" }, ...deps.map((d) => ({ label: d, value: d }))]} className="w-[220px]" />
        </Field>
      </FilterCard>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {!data || data.matrix.length === 0 ? (
            <EmptyState icon={CalendarRange} title="Belum ada data" desc="Belum ada capaian untuk filter ini." />
          ) : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full border-collapse text-sm" data-testid="rekap-matrix">
                <thead>
                  <tr className="bg-primary text-primary-foreground">
                    <th className="px-3 py-2.5 text-left sticky left-0 bg-primary z-10 min-w-[180px]">Poliklinik</th>
                    <th className="px-2 py-2.5 text-center">Target</th>
                    {MONTHS.map((m) => (<th key={m.n} className="px-2 py-2.5 text-center min-w-[56px]">{m.short}</th>))}
                    <th className="px-2 py-2.5 text-center bg-brand-deep min-w-[64px]">Rata2</th>
                  </tr>
                </thead>
                <tbody>
                  {data.matrix.map((m) => (
                    <tr key={m.poliklinik_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`matrix-row-${m.poliklinik_id}`}>
                      <td className="px-3 py-1.5 font-medium text-slate-700 sticky left-0 bg-white z-10">
                        {m.nama_poliklinik}
                        <div className="text-[10px] text-slate-400">{m.nama_departemen}</div>
                      </td>
                      <td className="px-2 py-1.5 text-center text-xs tabular-nums text-slate-500 border-r border-slate-100">{fmtPct(m.target)}</td>
                      {MONTHS.map((mo) => cell(mo.n, m.bulan[mo.n], m.target))}
                      <td className="px-2 py-1.5 text-center text-xs font-bold tabular-nums bg-brand-mist text-brand-deep">{fmtPct(m.rata_rata)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-100 font-bold border-t-2 border-slate-300">
                    <td className="px-3 py-2 sticky left-0 bg-slate-100 z-10">Rata-rata Bulanan</td>
                    <td className="border-r border-slate-200"></td>
                    {MONTHS.map((mo) => (
                      <td key={mo.n} className="px-2 py-2 text-center text-xs tabular-nums border-r border-slate-200">{fmtPct(data.footer[mo.n])}</td>
                    ))}
                    <td className="px-2 py-2 text-center text-xs tabular-nums bg-brand-sky/25 text-brand-deep">{fmtPct(data.footer_avg)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
